import { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Helmet } from 'react-helmet-async';
import { useRouter, Redirect } from 'react-resource-router';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { ROUTES } from '@app/navigation';
import { fg, featureGateKeys } from '@app/feature-gates';
import { BetaBanner } from './components/BetaBanner';
import { AllChangesContent } from './AllChangesContent';
import { BundledChangesContent } from './BundledChangesContent';
const ALL_CHANGES_TAB_INDEX = 0;
const BUNDLED_CHANGES_TAB_INDEX = 1;

const tabPanelContentStyles = xcss({
  width: '100%',
});

const messages = defineMessages({
  listPageTitle: {
    id: 'app.updates.title',
    defaultMessage: "What's new across Atlassian",
    description: 'Title for the app updates list page',
  },
  listPageSubtitle: {
    id: 'app.updates.subtitle',
    defaultMessage:
      "Discover new cloud features designed to unleash your team's potential and see how our latest updates can power your work today.",
    description: 'Subtitle describing the app updates list page content',
  },
  browserTabTitle: {
    id: 'app.updates.browserTabTitle',
    defaultMessage: 'Release Notes | Atlassian Community',
    description: 'Browser tab title for the release notes list page',
  },
  allChangesTab: {
    id: 'app.updates.tabs.allChanges',
    defaultMessage: 'All changes',
    description: 'Label for the "All changes" tab on the app updates list page',
  },
  bundledChangesTab: {
    id: 'app.updates.tabs.bundledChanges',
    defaultMessage: 'Release tracks changes',
    description:
      'Label for the "Bundled changes" tab on the app updates list page',
  },
});

export const AppUpdatesListPage: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const [routerState, routerActions] = useRouter();

  const isBundledChangesEnabled = fg(featureGateKeys.gaReleaseChanges);
  const isOnBundledChangesPath =
    routerState.location.pathname === ROUTES.RELEASE_TRACKS_CHANGES;

  if (isOnBundledChangesPath && !isBundledChangesEnabled) {
    return <Redirect to={ROUTES.RELEASE_NOTES} />;
  }

  const getActiveTabIndex = () => {
    if (!isBundledChangesEnabled) {
      return ALL_CHANGES_TAB_INDEX;
    }

    if (isOnBundledChangesPath) {
      return BUNDLED_CHANGES_TAB_INDEX;
    }

    return ALL_CHANGES_TAB_INDEX;
  };

  const handleTabChange = (index: number) => {
    // TODO: analytics — fire a tab-switch UI event here (specifics TBD).
    const nextPath =
      index === BUNDLED_CHANGES_TAB_INDEX
        ? ROUTES.RELEASE_TRACKS_CHANGES
        : ROUTES.RELEASE_NOTES;

    if (nextPath !== routerState.location.pathname) {
      routerActions.push(nextPath);
    }
  };

  return (
    <Box as="main" paddingBlockStart="space.300">
      <Helmet>
        <title>{formatMessage(messages.browserTabTitle)}</title>
      </Helmet>
      <Stack space="space.400">
        <Stack space="space.100">
          <Heading size="xxlarge">
            {formatMessage(messages.listPageTitle)}
          </Heading>
          <Text color="color.text.subtlest">
            {formatMessage(messages.listPageSubtitle)}
          </Text>
        </Stack>

        <BetaBanner />
        {isBundledChangesEnabled ? (
          <Tabs
            id="app-updates-tabs"
            selected={getActiveTabIndex()}
            onChange={handleTabChange}
          >
            <TabList>
              <Tab>{formatMessage(messages.allChangesTab)}</Tab>
              <Tab>{formatMessage(messages.bundledChangesTab)}</Tab>
            </TabList>
            <TabPanel testId="all-changes-panel">
              <Box paddingBlockStart="space.400" xcss={tabPanelContentStyles}>
                <AllChangesContent />
              </Box>
            </TabPanel>
            <TabPanel testId="bundled-changes-panel">
              <Box paddingBlockStart="space.400" xcss={tabPanelContentStyles}>
                <BundledChangesContent />
              </Box>
            </TabPanel>
          </Tabs>
        ) : (
          <AllChangesContent />
        )}
      </Stack>
    </Box>
  );
};
